import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye } from 'lucide-react';
import { HostSettingsCard, HostToggleRow } from './HostSettingsCard';
import { fetchAccountSettings, updateAccountPrivacy } from '../utils/accountSettingsApi';

/**
 * "Read receipts" privacy card — shown across guest, host and venue
 * settings. WhatsApp-style symmetry: when a user turns this OFF they stop
 * sending seen updates AND stop receiving them on their own messages.
 * The "Delivered" (double grey tick) state is unaffected.
 *
 * Backend enforcement lives in `messaging_conversations.py`
 * (mask_read_state_for_client + emit_conversation_status_update).
 */
export default function AllowReadReceiptsCard({ dataTestId = 'allow-read-receipts-card' }) {
  const [allowReceipts, setAllowReceipts] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchAccountSettings()
      .then((settings) => {
        setAllowReceipts(settings.privacy?.allow_read_receipts !== false);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const handleToggle = async () => {
    const next = !allowReceipts;
    setAllowReceipts(next);
    try {
      await updateAccountPrivacy({ allow_read_receipts: next });
      toast.success(next ? 'Read receipts are on' : 'Read receipts turned off');
    } catch (err) {
      setAllowReceipts(!next);
      toast.error(err.response?.data?.detail || 'Failed to update read-receipt preference');
    }
  };

  if (!loaded) return null;

  return (
    <HostSettingsCard data-testid={dataTestId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <Eye size={24} color="var(--text)" />
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: 0 }}>Read receipts</p>
      </div>

      <HostToggleRow
        label="Allow others to know when I read their messages"
        description="Turn off to hide the blue 'Seen' tick from the other side. You will also stop seeing when your own messages are read — the trade is symmetric."
        checked={allowReceipts}
        onChange={handleToggle}
        dataTestId={`${dataTestId}-toggle`}
      />
    </HostSettingsCard>
  );
}
